import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { ErrorSurface } from "@/components/error-surfaces/error-surface";
import { reloadPage } from "@/components/error-surfaces/reload-page";
import { Button } from "@/components/ui/button";

/**
 * The 500 surface: the app's default for anything that threw and isn't an
 * expired session or a permission wall (components/router-fallbacks.tsx).
 *
 * `onReload` exists because a router error is usually recoverable without a
 * document load — the caller passes the in-place retry (invalidate + reset the
 * Query error boundary). The default is `reloadPage`, for a caller with nothing
 * cheaper to offer.
 */
export function ServerError({
  onReload,
  detail,
}: {
  onReload?: () => void;
  /** Extra explanation — a message Python wrote for the user, or dev-only raw detail. */
  detail?: ReactNode;
}) {
  return (
    <ErrorSurface
      code="500"
      title="Something went wrong on our end"
      message="We hit an unexpected problem while loading this. Please try again in a moment."
      detail={detail}
      actions={
        <>
          <Button className="w-full" onClick={onReload ?? reloadPage}>
            Reload
          </Button>
          <Button asChild variant="outline" className="w-full">
            {/* Home is `/`, the landing dispatcher — so this follows the
                audience's landing path rather than naming a destination. */}
            <Link to="/">Go home</Link>
          </Button>
        </>
      }
    />
  );
}
